import { SyntheticEvent } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { ToastSwal } from '../../../../components/components/general/toastSwals/toastSwal';
import {
    asyncGetSendCode,
    asyncGetValidateCode,
} from '../../../../services/auth.service';
import {
    asyncPatchtUserPassword,
    asyncGetUserByEmail,
} from '../../../../services/users.service';
import { UsersMessages } from '../../../../shared/messages/usersMessages';
import { SIGNIN } from '../../../../routes/paths';

export const useUpdatePassword = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const [sendCode, setSendCode] = useState(false);
    const [validationCode, setValidationCode] = useState<boolean>(false);
    const [timeLeftForward, setTimeLeftForward] = useState(0);
    const [isForwardDisabled, setIsForwardDisabled] = useState<boolean>(false);
    const [idUser, setIdUser] = useState('');
    const [showPasswordRestrictionModal, setShowPasswordRestrictionModal] =
        useState(false);
    const [userEmailForModal, setUserEmailForModal] = useState('');

    const navigate = useNavigate();
    const { usersMessages }: any = UsersMessages;

    const formik = useFormik({
        initialValues: {
            email: '',
            password: '',
            password_retry: '',
        },
        validationSchema: Yup.object({
            email: Yup.string()
                .required('El correo es obligatorio')
                .email('El formato del correo es incorrecto'),
            password: Yup.string()
                .required('La contraseña es obligatoria')
                .min(8, 'La contraseña debe tener al menos 8 caracteres')
                .matches(
                    /^[a-zA-Z0-9!@#$%^&*()_+\-]+$/,
                    'La contraseña puede incluir caracteres alfanuméricos y los siguientes caracteres especiales: ! @ # $ % ^ & * ( ) _ + -'
                )
                .matches(
                    /[!@#$%^&*()_+\-]/,
                    'La contraseña debe contener al menos un carácter especial: ! @ # $ % ^ & * ( ) _ + -'
                ),
            password_retry: Yup.string().when(['password'], {
                is: (password: any) => password !== undefined,
                then: (schema) =>
                    schema
                        .required(
                            'La confirmación de la contraseña es obligatoria'
                        )
                        .oneOf(
                            [Yup.ref('password')],
                            'Las contraseñas no coinciden'
                        ),
            }),
        }),
        onSubmit: async (formik) => {
            if (idUser !== undefined) {
                asyncPatchtUserPassword(idUser, formik).then((body: any) => {
                    if (body?.items !== undefined) {
                        ToastSwal.fire({
                            text: usersMessages.MESSAGE_EDIT,
                            icon: 'success',
                        });
                        navigate(SIGNIN);
                    } else if (body?.errors !== undefined) {
                        ToastSwal.fire({
                            text: usersMessages[body.message],
                            icon: 'error',
                        });
                    }
                });
            } else {
                ToastSwal.fire({
                    text: usersMessages.USER_NOT_FOUND,
                    icon: 'error',
                });
            }
        },
    });

    const formikValidationCode = useFormik({
        initialValues: {
            code: '',
        },
        validationSchema: Yup.object({
            code: Yup.string().required(
                'El código de verificación es obligatorio'
            ),
        }),
        onSubmit: async (formValidation) => {
            asyncGetValidateCode(
                `<${formik.values.email}>` || '',
                formValidation.code || ''
            ).then((res) => {
                if (res?.status && typeof res?.status === 'boolean') {
                    setValidationCode(true);
                    ToastSwal.fire({
                        text: `Código validado correctamente`,
                        icon: 'success',
                        timer: 5000,
                        position: 'center-end',
                    });
                } else {
                    setValidationCode(false);
                    ToastSwal.fire({
                        text: `Código invalido. Por favor intenta de nuevo.`,
                        icon: 'error',
                        timer: 5000,
                        position: 'center-end',
                    });
                    formik?.setFieldValue('code', formValidation.code || '');
                }
            });
        },
    });

    const handleClickSend = (e: SyntheticEvent) => {
        e.preventDefault();
        setLoading(true);
        asyncGetUserByEmail(formik.values.email).then((resp: any) => {
            if (resp && resp.items) {
                if (resp.items.password_change) {
                    setIdUser(resp.items.id);
                    asyncGetSendCode(`<${formik.values.email}>`)
                        .then((res) => {
                            if (
                                res &&
                                res?.statusCode &&
                                res?.statusCode === 200
                            ) {
                                setSendCode(true);
                                setIsForwardDisabled(true);
                                setTimeLeftForward(480);
                                setTimeout(() => {
                                    setIsForwardDisabled(false);
                                }, 480000);
                                ToastSwal.fire({
                                    text: `Código enviado. Por favor, revisa tu bandeja de entrada.`,
                                    icon: 'success',
                                    timer: 5000,
                                    position: 'center-end',
                                });
                            } else {
                                setSendCode(false);
                                ToastSwal.fire({
                                    text: `Lo sentimos, no se pudo enviar el código. Por favor intenta de nuevo.`,
                                    icon: 'error',
                                    timer: 5000,
                                    position: 'center-end',
                                });
                            }
                        })
                        .finally(() => setLoading(false));
                } else {
                    setUserEmailForModal(formik.values.email);
                    setShowPasswordRestrictionModal(true);
                    setLoading(false);
                }
            } else {
                ToastSwal.fire({
                    text: `Lo sentimos, ${usersMessages.USER_NOT_FOUND}. Por favor verifica el correo e intenta de nuevo.`,
                    icon: 'error',
                    timer: 5000,
                    position: 'center-end',
                });
                setLoading(false);
            }
        });
    };

    useEffect(() => {
        setLoading(false);
        if (timeLeftForward > 0) {
            const timer = setInterval(() => {
                setTimeLeftForward((prevTime) => prevTime - 1);
            }, 1000);
            return () => clearInterval(timer);
        }
    }, [timeLeftForward]);

    return {
        formik,
        formikValidationCode,
        loading,
        sendCode,
        timeLeftForward,
        validationCode,
        handleClickSend,
        isForwardDisabled,
        idUser,
        showPasswordRestrictionModal,
        setShowPasswordRestrictionModal,
        userEmailForModal,
    };
};
